import { useCallback, useEffect, useReducer, useRef } from 'react';
import { streamChat } from '../api/sseClient';
import { EVENTS } from '../api/contract';
import { ACTIONS, chatReducer, initialChatState } from '../state/chatReducer';

let seq = 0;
const nextId = (prefix) => {
  seq += 1;
  return `${prefix}-${Date.now().toString(36)}-${seq}`;
};

const raf = (cb) => (typeof window !== 'undefined' && window.requestAnimationFrame
  ? window.requestAnimationFrame(cb)
  : setTimeout(cb, 16));
const cancelRaf = (handle) => (typeof window !== 'undefined' && window.cancelAnimationFrame
  ? window.cancelAnimationFrame(handle)
  : clearTimeout(handle));

/**
 * Wires the SSE client to the chat reducer.
 *
 * Deltas are buffered in a ref and flushed once per animation frame (one render per frame); every other
 * event flushes the buffer first so the order of text and events is kept.
 *
 * @param {{url: string, buildBody: function(string): Object, onTurnDone?: function(Object): void,
 *          onError?: function(Object): void, messages?: Object, streamImpl?: function}} options
 */
export default function useChatStream({ url, buildBody, onTurnDone, onError, messages, streamImpl }) {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const pending = useRef({ id: null, text: '' });
  const frame = useRef(null);
  const controllerRef = useRef(null);
  const activeRef = useRef(null);
  const turnRef = useRef(null);
  const callbacks = useRef({});
  callbacks.current = { buildBody, onTurnDone, onError, messages, url, streamImpl };

  const flush = useCallback(() => {
    if (frame.current != null) {
      cancelRaf(frame.current);
      frame.current = null;
    }
    const { id, text } = pending.current;
    if (id && text) {
      pending.current = { id, text: '' };
      dispatch({ type: ACTIONS.DELTA_BATCH, id, text });
    }
  }, []);

  const queueDelta = useCallback((id, text) => {
    if (pending.current.id !== id) {
      flush();
      pending.current = { id, text: '' };
    }
    pending.current.text += text;
    if (frame.current == null) {
      frame.current = raf(() => {
        frame.current = null;
        flush();
      });
    }
  }, [flush]);

  const run = useCallback(async (assistantId, question) => {
    const { buildBody: build, url: endpoint, streamImpl: impl } = callbacks.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    activeRef.current = assistantId;
    const conversationId = stateRef.current.conversationId;
    const body = { ...(build ? build(question) : { question }) };
    if (conversationId && !body.conversation_id) body.conversation_id = conversationId;

    const turn = { question, assistantId, messageId: '', conversationId, text: '' };
    turnRef.current = turn;
    const onEvent = (event) => {
      if (activeRef.current !== assistantId) return; // a stopped/replaced turn
      switch (event.type) {
        case EVENTS.DELTA:
          queueDelta(assistantId, event.text || '');
          return;
        case EVENTS.META:
          flush();
          turn.messageId = event.message_id || turn.messageId;
          turn.conversationId = event.conversation_id || turn.conversationId;
          dispatch({ type: ACTIONS.META, id: assistantId, event });
          return;
        case EVENTS.STATUS:
          flush();
          dispatch({ type: ACTIONS.STATUS, id: assistantId, event });
          return;
        case EVENTS.SOURCES:
          flush();
          dispatch({ type: ACTIONS.SOURCES, id: assistantId, event });
          return;
        case EVENTS.ANSWER_END:
          flush();
          turn.text = event.text || '';
          dispatch({ type: ACTIONS.ANSWER_END, id: assistantId, event });
          return;
        case EVENTS.FOLLOWUPS:
          flush();
          dispatch({ type: ACTIONS.FOLLOWUPS, id: assistantId, event });
          return;
        case EVENTS.DONE:
          flush();
          turn.messageId = event.message_id || turn.messageId;
          turn.conversationId = event.conversation_id || turn.conversationId;
          dispatch({ type: ACTIONS.DONE, id: assistantId, event });
          if (callbacks.current.onTurnDone) callbacks.current.onTurnDone({ ...turn });
          return;
        case EVENTS.ERROR:
          flush();
          dispatch({ type: ACTIONS.ERROR, id: assistantId, event });
          if (callbacks.current.onError) callbacks.current.onError(event);
          return;
        default:
          // Unknown event types are ignored (forward compatibility).
      }
    };

    try {
      await (impl || streamChat)({
        url: endpoint,
        body,
        signal: controller.signal,
        onEvent,
        messages: callbacks.current.messages,
      });
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      if (activeRef.current === assistantId) {
        flush();
        activeRef.current = null;
      }
    }
  }, [flush, queueDelta]);

  const send = useCallback((question, { mode } = {}) => {
    const text = String(question || '').trim();
    if (!text || activeRef.current) return null;
    const userId = nextId('u');
    const assistantId = nextId('a');
    dispatch({ type: ACTIONS.SEND, userId, assistantId, question: text, mode });
    run(assistantId, text);
    return assistantId;
  }, [run]);

  const stop = useCallback(() => {
    const id = activeRef.current;
    if (!id) return;
    flush();
    activeRef.current = null;
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = null;
    dispatch({ type: ACTIONS.STOP, id });
    // The backend saves the partial answer (status "stopped"), so the conversation exists in history:
    // report the turn like a finished one so the sidebar shows it.
    const turn = turnRef.current;
    if (turn && turn.assistantId === id && turn.conversationId && callbacks.current.onTurnDone) {
      callbacks.current.onTurnDone({ ...turn, stopped: true });
    }
  }, [flush]);

  const retry = useCallback((id) => {
    if (activeRef.current) return;
    const message = stateRef.current.messages.find((m) => m.id === id);
    if (!message || !message.question) return;
    dispatch({ type: ACTIONS.RETRY, id });
    run(id, message.question);
  }, [run]);

  const reset = useCallback(() => {
    flush();
    activeRef.current = null;
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = null;
    dispatch({ type: ACTIONS.RESET });
  }, [flush]);

  const loadHistory = useCallback((historyMessages, conversationId, conversationInfo) => {
    flush();
    activeRef.current = null;
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = null;
    dispatch({
      type: ACTIONS.LOAD_HISTORY, messages: historyMessages, conversationId, conversationInfo,
    });
  }, [flush]);

  /** Prepend older messages (lazy history paging). */
  const prependHistory = useCallback((olderMessages) => {
    if (olderMessages && olderMessages.length) dispatch({ type: ACTIONS.PREPEND_HISTORY, messages: olderMessages });
  }, []);

  useEffect(() => () => {
    if (controllerRef.current) controllerRef.current.abort();
    if (frame.current != null) cancelRaf(frame.current);
  }, []);

  return {
    state,
    busy: state.activeId != null,
    send,
    stop,
    retry,
    reset,
    loadHistory,
    prependHistory,
  };
}
